<template>
  <div class="containerStepRecorder">

    <!-- Mode tabs: full width, above the side drawers -->
    <div class="layoutSelector" :class="isRecording?'hide':''">
      <ScreenLayoutSelector ref="screenSelector" @input="uploadFile" @close="onClose"/>
    </div>

    <!-- Next step, in the header -->
    <ValidateWeetButton data-test="recorderNextButtonStep1"
                        stepTour="buttonFinish" class="headerAction" icon="arrow-right"
                        color="primary"
                        @click="clickNext"/>

    <div class="bodyRow">

    <!-- Tool bar: opens the side panels, which stay closed until asked for -->
    <div class="recorderToolBar" v-if="!isRecording">
      <w-button v-for="tool in tools" :key="'tool_'+tool.key"
                class="toolButton" :class="{selected: activePanel===tool.key}"
                :data-test="tool.dataTest"
                :icon="tool.icon" size="default"
                :color="activePanel===tool.key?'primary':'outlined'"
                :tooltip="tool.label" tooltip-position="right"
                @click="togglePanel(tool.key)"/>
    </div>

    <RecorderDrawer side="left" class="sideDrawer" v-if="isLeftPanelOpen">
      <AIAvatarSlideTemplatePanel class="drawerPanel filterDrawerPanel" v-if="activePanel==='layout'" @close="activePanel=''"/>
      <AIAvatarSelectionPanel class="drawerPanel filterDrawerPanel" v-else-if="activePanel==='avatar'" @close="activePanel=''"/>
      <AIAvatarBackgroundSelectionPanel class="drawerPanel filterDrawerPanel" v-else-if="activePanel==='background'" @close="activePanel=''"/>
      <RecorderStickerSettingsPanel class="drawerPanel" v-else-if="activePanel==='settings'"/>
      <FilterPanel class="drawerPanel filterDrawerPanel" :big="true" v-else-if="activePanel==='filters'"
                   @close="activePanel=''"/>

      <VirtualScreenCastPanel class="drawerPanel filterDrawerPanel" :big="true"
                              v-else-if="activePanel==='scene'" @close="activePanel=''"/>
    </RecorderDrawer>

    <div class="mainColumn" :class="{withScriptPanel: isScriptPanelOpen}">
      <div class="middleZone">
        <div class="chooseScreenLayout" >
          <ChooseScreen @screenLoaded="onScreenLoaded"
                        @killSource="stopRecordIfNeed"
                        @onAIGenerated="onAIGenerated"
                        @closeRecorder="onClose"/>

          <div class="stickerLayout">
            <StickerLayoutSource @openFilter="onOpenFilter"/>
          </div>

          <div class="timerBlock" v-if="(!avatarAIStep || avatarAIStep<=1) && !isAvaterOptionPanelOpened ">
            <w-icon icon="clock-time-two-outline"
                    color="text" size="small"/>
            {{ humanTimerRecord }} / {{ humanTimeMax }}
          </div>
        </div>
      </div>

      <!-- Record the events in the timeline -->
      <TimeLineRecorder/>


      <div class="bottomButtonBar">
        <!-- BUTTON BAR -->
        <div class="stickerControls" v-if="showStickerControls && !isRecording">
          <StickerMicMuteButton color="outlined" size="default"/>
          <StickerVideoMuteButton color="outlined" size="default"/>
          <StickerRemoveStickerButton color="outlined" size="default"/>
        </div>

        <RecorderStartPauseButton stepTour="recordStartButton"
                                  class="buttonRecord" :buttonMode="true"
                                  @click="toggleRecord" v-if="!isAvatarAI" />
        <GenerateAvatarButton v-else />
        <!-- Component to count the record-->
        <TimerRecorder v-show="false"/>
        <div class="sectionTimer" v-show="false">
          <SectionTimer @end="stopRecordIfNeed"/>
        </div>
      </div>
    </div>

    </div>

    <!-- Timeline: full width, below the side drawers -->
    <div class="timeLineView">
      <TimeLine :advanced-aivailable="true" :editing-available="true"
                :upload-available="true" @upload="onTimeLineUpload"/>
    </div>

    <CreateWeetUploadStep :withNoiseCancelling="true" :file="file" @close="onFileUploaded"
                          :queue-index="queueIndex" :queue-total="queueTotal"/>

    <OnboardingRecord class="onBoardingRecord" v-if="isRecordOnboarding && isShowOnboarding"/>
    <OnboardingSection class="onBoardingSection" v-if="isSectionOnboarding && isShowOnboarding"/>
    <!-- COUNT DOWN LAYOUT -->
    <transition name="fade">
      <div class="countDownContainer" v-show="countDown!==0" data-test="recorderCountDown">
        <div class="counter">
          {{ countDown }}
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Watch} from 'vue-property-decorator';
import WeetPlayer from '@/components/play/player/WeetPlayer.vue';
import store from '@/store';
import {Howl} from 'howler';
import ChooseScreen from '@/components/recorder/videoLayout/chooseScreen/ChooseScreen.vue';
import TimeLineRecorder from '@/components/recorder/timeLine/TimeLineRecorder.vue';
import StickerLayoutSource from '@/components/recorder/sticker/StickerLayoutSource.vue';
import RecorderStartPauseButton from '@/components/recorder/button/RecorderStartPauseButton.vue';
import TimerRecorder from '@/components/recorder/timer/TimerRecorder.vue';
import SectionTimer from '@/components/recorder/timer/SectionTimer.vue';
import ValidateWeetButton from '@/components/actionButton/ValidateWeetButton.vue';
import EndWeetButton from '@/components/actionButton/EndWeetButton.vue';
import {setActionEvent, setPageEvent} from '@/utils/tracker';
import {ActionEvent, PageEvent} from '@/enum/TrackerEnum';
import {
  PAUSE_RECORDING,
  SELECT_SEGMENT,
  START_RECORDING,
  STOP_RECORDING
} from "@/store/recordingState/recordStateAction";
import ScreenLayoutSelector from '@/components/recorder/videoLayout/ScreenLayoutSelector.vue';
import {VALIDATE_MEDIA} from '@/store/media/mediaAction';
import TimeLine from '@/components/recorder/timeLine/TimeLine.vue';
import {Weet} from '@/store/weet/weetModel';
import {SEND_VISIBILITY_POLICY_WEET} from '@/store/myWeet/myWeetAction';
import {sendBadgeMessage, sendMessageToExtention} from '@/utils/extentionUtil';
import {TIMER_RECORD} from '@/store/timeLine/timeLineAction';
import WIcon from '@/components/wrapper/w-icon.vue';
import prettyMilliseconds from 'pretty-ms';
import OnboardingRecord from '@/components/onboarding/OnboardingRecord.vue';
import OnboardingSection from '@/components/onboarding/OnboardingSection.vue';
import {numberOfSection} from '@/utils/timeLineUtils';
import {embededInExternalIframe} from '@/utils/integrationUtil';
import {VisibilityPolicy} from '@/store/weet/VisibilityPolicy';
import delay from 'delay';
import CreateWeetUploadStep from "@/components/recorder/createWeetSubComponent/CreateWeetUploadStep.vue";
import {UPLOAD_FILE_DESCRIPTION} from "@/store/upload/uploadAction";
import WorkspaceMovePanel from "@/components/workspace/WorkspaceMovePanel.vue";
import {initWeetEditing} from "@/utils/createWeetUtil";
import {SourceVideoType} from "@/enum/SourceVideoEnum";
import WButton from "@/components/wrapper/w-button.vue";
import GenerateAvatarButton from "@/components/recorder/button/GenerateAvatarButton.vue";
import {PUBLIC_PATH} from "@/store/conf/api";
import {SpeachEvent, SpeachEventName, SpeachModification} from "@/speach/speachEvent";
import {isSpeach, sendSilentWeetId} from "@/utils/speachUtils";
import { hasSlideWorkInProgress } from "@/utils/slides/slideUtil";
import { confirmationDanger } from "@/utils/dialog";
import { OPEN_AVATAR_EDITING_CONTENT } from "@/store/AIAvatar/AIAVatarAction";
import RecorderDrawer from "@/components/recorder/createWeetSubComponent/RecorderDrawer.vue";
import AIAvatarSelectionPanel from "@/components/recorder/aiAvatar/panel/AIAvatarSelectionPanel.vue";
import AIAvatarBackgroundSelectionPanel
  from "@/components/recorder/aiAvatar/panel/AIAvatarBackgroundSelectionPanel.vue";
import AIAvatarSlideTemplatePanel from "@/components/recorder/aiAvatar/panel/AIAvatarSlideTemplatePanel.vue";
import RecorderStickerSettingsPanel from "@/components/recorder/sticker/panel/RecorderStickerSettingsPanel.vue";
import StickerMicMuteButton from "@/components/recorder/sticker/buttons/StickerMicMuteButton.vue";
import StickerVideoMuteButton from "@/components/recorder/sticker/buttons/StickerVideoMuteButton.vue";
import StickerRemoveStickerButton from "@/components/recorder/sticker/buttons/StickerRemoveStickerButton.vue";
import VirtualScreenCastPanel from "@/components/recorder/videoLayout/chooseScreen/VirtualScreenCastPanel.vue";
import FilterPanel from "@/components/recorder/sticker/buttons/FilterPanel.vue";
import {WebCamIntegrationTypeEnum} from "@/enum/WebCamIntegrationTypeEnum";
import {StickerPosition} from "@/enum/StickerPositionEnum";


@Component({
  components: {
    RecorderDrawer,
    RecorderStickerSettingsPanel,
    StickerMicMuteButton,
    StickerVideoMuteButton,
    StickerRemoveStickerButton,
    VirtualScreenCastPanel,
    FilterPanel,
    AIAvatarSlideTemplatePanel,
    AIAvatarSelectionPanel,
    AIAvatarBackgroundSelectionPanel,
    GenerateAvatarButton,
    WButton,
    WorkspaceMovePanel,
    CreateWeetUploadStep,
    OnboardingSection,
    OnboardingRecord,
    WIcon,
    TimeLine,
    ScreenLayoutSelector,
    EndWeetButton,
    ValidateWeetButton,
    SectionTimer,
    TimerRecorder, RecorderStartPauseButton, StickerLayoutSource, TimeLineRecorder, ChooseScreen, WeetPlayer
  },
})
export default class CreateWeetRecorderStep extends Vue {

  private countDown: number = 0;
  private sound: Howl = new Howl({src: [PUBLIC_PATH+'sound/click2.mp3']});
  private filterOpen: boolean = false;

  private file: File | null = null;
  // multi upload from the timeline: files waiting after the current one
  private pendingFiles: File[] = [];
  private queueIndex: number = 1;
  private queueTotal: number = 1;

  // side panel currently open, empty when they are all closed
  private activePanel: string = '';

  get weetID(): string {
    return store.getters.getEditingWeetID;
  }

  get isRecording(): boolean {
    return store.getters.isPauseAivailable || store.getters.isAvatarGenerateInProgress;
  }

  get isAvatarAI():boolean{
    return store.getters.getMainRecordSource===SourceVideoType.AIAVATAR;
  }

  // must match the panel's own condition in AIAvatarSource: it stays hidden
  // while the document is being processed
  get isScriptPanelOpen(): boolean {
    return this.isAvatarAI
        && store.getters.isOpenAvatarEditionContent
        && !store.getters.isProceedSlideInProgress;
  }

  get isBackgroundMode(): boolean {
    return store.getters.getMainRecordSource === SourceVideoType.VIRTUAL_SCREENCAST;
  }

  // Buttons of the left tool bar, depending on the current recording mode.
  get tools(): Array<{ key: string, icon: string, label: string, dataTest?: string }> {
    if (this.isAvatarAI) {
      return [
        {key: 'layout', icon: 'view-dashboard-outline', label: this.$t('aiAvatar.drawer.layout').toString()},
        {key: 'avatar', icon: 'account-edit-outline', label: this.$t('aiAvatar.drawer.avatar').toString()},
        {key: 'background', icon: 'image-outline', label: this.$t('aiAvatar.drawer.background').toString()},
      ];
    }
    if (this.isCameraMode) {
      const tools: Array<{ key: string, icon: string, label: string, dataTest?: string }> = [
        {
          key: 'settings', icon: 'cog-outline',
          label: this.$t('recorderDrawer.settings').toString(), dataTest: 'recorderSettingButton'
        },
      ];
      if (this.isWebcamActive) {
        tools.push({
          key: 'filters', icon: 'auto-fix',
          label: this.$t('recorderDrawer.filters').toString(), dataTest: 'recorderFilterButton'
        });
      }
      if (this.isBackgroundMode) {
        tools.push({key: 'scene', icon: 'brush', label: this.$t('svb.title').toString()});
      }
      return tools;
    }
    return [];
  }

  // mic/camera/sticker toggles: pointless in webcam full screen and webcam integration,
  // where the sticker is not displayed and the toggles are disabled anyway
  get showStickerControls(): boolean {
    if (!this.isCameraMode || store.getters.getMainRecordSource === SourceVideoType.WEBCAM) {
      return false;
    }
    return !(store.getters.getMainRecordSource === SourceVideoType.SCREENCAST
        && store.getters.getWebCamIntegration !== WebCamIntegrationTypeEnum.NONE);
  }

  get isLeftPanelOpen(): boolean {
    return ['layout', 'avatar', 'background', 'settings', 'filters', 'scene'].indexOf(this.activePanel) > -1;
  }

  private togglePanel(key: string) {
    this.activePanel = this.activePanel === key ? '' : key;
  }

  // a panel of the previous mode must not stay open after switching
  @Watch('tools')
  private onToolsChanged() {
    if (this.activePanel && !this.tools.find((tool) => tool.key === this.activePanel)) {
      this.activePanel = '';
    }
  }

  // the tool bar is hidden while recording: close what it opened
  @Watch('isRecording')
  private onRecordingChanged(recording: boolean) {
    if (recording) {
      this.activePanel = '';
    }
  }

  // modes relying on the camera: screen, background (virtual screencast) and webcam
  get isCameraMode(): boolean {
    const source = store.getters.getMainRecordSource;
    return source === SourceVideoType.SCREENCAST
        || source === SourceVideoType.VIRTUAL_SCREENCAST
        || source === SourceVideoType.WEBCAM;
  }

  // filters only make sense while the webcam actually streams
  get isWebcamActive(): boolean {
    if (!store.getters.isWebcamAivalable) {
      return false;
    }
    if (store.getters.getMainRecordSource === SourceVideoType.WEBCAM) {
      return true;
    }
    if (store.getters.getMainRecordSource === SourceVideoType.SCREENCAST
        && store.getters.getWebCamIntegration !== WebCamIntegrationTypeEnum.NONE) {
      return false;
    }
    return !store.getters.isStickerVideoMute
        && store.getters.getStickerPosition !== StickerPosition.NONE;
  }

  get humanTimerRecord(): string {
    return prettyMilliseconds(store.getters.getTimerRecord, {secondsDecimalDigits: 0, colonNotation: true});
  }

  get humanTimeMax(): string {
    return prettyMilliseconds(this.timeMax, {colonNotation: true, secondsDecimalDigits: 0});
  }

  get timeMax(): number {
    // convert second in millisecond
    return store.getters.getWeetMaxTime * 1000;
  }

  get isTimeLineFullAivailable(): boolean {
    return !this.isRecording && this.countDown === 0;
  }

  get myWeet(): Weet | undefined {
    return store.getters.getMyWeet(store.getters.getEditingWeetID);
  }

  get isRecordOnboarding(): boolean {
    if (this.myWeet) {
      return !store.getters.isOnboardingRecorder1 && store.getters.getTimeEvent.length === 0;
    } else {
      return !store.getters.isOnboardingRecorder1;
    }
  }

  get avatarAIStep(){
    return store.getters.getAiAvatarStep;
  }

  get isAvaterOptionPanelOpened(){
    return store.getters.isPanelOptionOpen
  }

  get isShowOnboarding(): boolean {
    return !this.isRecording && this.countDown === 0 && !this.filterOpen && !embededInExternalIframe();
  }

  get isSectionOnboarding(): boolean {
    if (this.myWeet) {
      return !store.getters.isOnboardingSection1 && numberOfSection(this.myWeet.timeLine) === 2;
    } else {
      return false;
    }
  }

  private uploadFile(file: File): void {
    this.file = file;
  }

  // the upload tile lives in the timeline, but the source switch stays in the selector
  private async onTimeLineUpload(files: File[]): Promise<void> {
    // set before chooseFile so the upload step knows its position when the first file arrives
    this.pendingFiles = files.slice(1);
    this.queueTotal = files.length;
    this.queueIndex = 1;
    // @ts-ignore
    await this.$refs.screenSelector.chooseFile(files[0]);
    if (this.file !== files[0]) {
      // the user refused to leave the AI avatar: drop the batch
      this.resetUploadQueue();
    }
  }

  // success=false means the user cancelled: the whole batch is dropped
  private onFileUploaded(success: boolean): void {
    store.dispatch(UPLOAD_FILE_DESCRIPTION, null);
    const nextFile = success ? this.pendingFiles.shift() : undefined;
    if (nextFile) {
      // the files are uploaded one after the other, each one creates its own section
      this.queueIndex++;
      this.file = nextFile;
      return;
    }
    this.file = null;
    this.resetUploadQueue();
    this.reinitAIAvatar();
  }

  private resetUploadQueue(): void {
    this.pendingFiles = [];
    this.queueIndex = 1;
    this.queueTotal = 1;
  }
  private onAIGenerated():void{
    this.reinitAIAvatar();
  }

  private onClose(): void {
    this.$emit('close');
  }

  public async toggleRecord() {
    // ignore if countdown is alredy
    // avoid spam extention click
    if (this.countDown > 0) {
      return;
    }

    if (this.isRecording) {
      setActionEvent(ActionEvent.record_button_pause, {record_time: store.getters.getTimerRecord});
      store.dispatch(PAUSE_RECORDING);
      this.validateSection();
    } else {

      this.countDown = 3;
      this.$emit('countDown', this.countDown);
      this.sound.play();

      // we create the weet if no do
      this.initWeetEditing();

      const decCountDown = () => {
        this.countDown--;
        this.$emit('countDown', this.countDown);
        sendBadgeMessage(this.countDown + '', '#9600ff');
        this.sound.play();
      };
      setTimeout(decCountDown, 1000);
      setTimeout(decCountDown, 2000);
      setTimeout(() => {
        this.sound.play();
        setTimeout(async () => {
          // Wait editingID arrive
          while (!store.getters.getEditingWeetID) {
            await delay(500);
          }
          this.countDown--;
          this.$emit('countDown', this.countDown);
          setActionEvent(ActionEvent.record_button_record, {
            filter: store.getters.getRecorderFilter,
            start_record_time: store.getters.getTimerRecord
          });

          store.dispatch(START_RECORDING);
        }, 300);
      }, 3000);
    }
  }

  private mounted() {
    sendMessageToExtention(TIMER_RECORD, 0);
    sendMessageToExtention(STOP_RECORDING);
    setPageEvent(PageEvent.page_record_step_record);
  }

  /**
   * Going to the preview destroys this step: slides that were never generated into a
   * video section would silently not be part of the video, so warn before leaving.
   */
  private async clickNext() {
    if (hasSlideWorkInProgress()) {
      try {
        await confirmationDanger(
            this.$t('createWeetComponent.previewWithSlides.description').toString(),
            this.$t('createWeetComponent.previewWithSlides.title').toString(),
            this.$t('createWeetComponent.previewWithSlides.confirm').toString(),
            this.$t('generic.cancel').toString(),
        );
      } catch (e) {
        // the user cancelled: stay on the recording step
        return;
      }
    }
    this.$emit('next');
  }

  private stopRecordIfNeed() {
    if (this.isRecording) {
      this.toggleRecord();
    }
    setTimeout(() => {
      this.reinitAIAvatar();
    }, 100);
  }

  private reinitVirtualBackground() {
    // @ts-ignore
    this.$refs.screenSelector.chooseVirtualScreenCast();
  }

  public reinitAIAvatar(){
    // @ts-ignore
    this.$refs.screenSelector.chooseAIAvatar();
  }

  private validateSection() {
    // we validate the media to Save the Media :-)
    store.dispatch(VALIDATE_MEDIA, store.getters.getMainMediaId);
    store.dispatch(VALIDATE_MEDIA, store.getters.getStickersMediaId);
  }

  private async initWeetEditing() {
    // before record we create an ID
    // this id must be provide before the first chunk upload
    if (store.getters.getEditingWeetID === '') {
      const weet=await initWeetEditing();

      // if we are in an external tool (Teams / Slack / Speach /)
      // we open the weet to be more accessible
      if (store.getters.getExternalTool) {
        store.dispatch(SEND_VISIBILITY_POLICY_WEET,
            {weetID: weet.weetID, policy: VisibilityPolicy.SEMI_PRIVATE});
      }
    }
  }



  private onScreenLoaded() {
    if (store.getters.isStartRecordingAivailable) {
      // TODO mettre une conf
      //  this.startCountDown();
    }
  }

  private onOpenFilter(value) {
    this.filterOpen = value;
  }

  beforeDestroy(){
    store.dispatch(SELECT_SEGMENT,null);
    // the slides are NOT dropped here: this step is also destroyed when going to the
    // preview, and coming back must find the deck untouched. The recorder session ends
    // in CreateWeetComponent.beforeDestroy, which is where the deck is dropped.
    store.dispatch(OPEN_AVATAR_EDITING_CONTENT,false)
  }
}
</script>

<style scoped lang="scss">

.containerStepRecorder {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background: var(--light);

  // everything below the mode tabs: drawers on the sides, content in the middle
  .bodyRow {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: row;
  }

  // Left tool bar: an invisible column, only the buttons show
  .recorderToolBar {
    flex: none;
    // 16px + 52px: the button lines up with the timeline card below, and the
    // drawer brings its own margin, so no right margin here
    width: 68px;
    padding-left: 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 12px;
    background: transparent;

    // square icon button: colours and hover are the button component's job
    .toolButton ::v-deep .button {
      width: 52px;
      padding-left: 0;
      padding-right: 0;
    }
  }

  .sideDrawer {
    flex: none;

    .drawerTabBar {
      display: flex;
      flex: none;
      height: 52px;
      border-bottom: solid 1px var(--light1);

      // single, non-clickable header (right drawer)
      .drawerTitle {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 900;
        color: var(--dark);
      }
    }

    .drawerPanel {
      padding: 8px;
    }

    // the filter panel splits its own height between filters and backgrounds
    .filterDrawerPanel {
      flex: 1 1 0;
      min-height: 0;
    }

  }

  .mainColumn {
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: padding-right 0.2s ease;

    // the script panel is a fixed overlay on the right:
    // reserve its width so the 16/9 zone stays fully visible
    &.withScriptPanel {
      padding-right: 336px;
    }
  }

  .layoutSelector {
    flex: none;
    width: 100%;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--white);
    box-shadow: 0 1px 4px rgba(10, 8, 47, 0.08);
    z-index: 10;
    margin-top: 0;
    margin-bottom: 0;

    &.hide {
      visibility: hidden;
    }
  }

  .bottomButtonBar {
    margin-top: 16px;
    margin-bottom: 32px;
    // position: absolute;
    width: 100%;
    min-height: 48px;
    left: 0px;
    justify-content: center;
    align-items: center;
    // background: red;
    bottom: 40px;
    display: flex;

    .buttonRecord {
      //margin-left: 130px;
      // margin-right: 16px;
    }

    // mic / camera / sticker, square like the tool bar buttons
    .stickerControls {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-right: 16px;

      ::v-deep .button {
        width: 52px;
        padding-left: 0;
        padding-right: 0;
      }
    }

  }

  // sits in the header bar, on the right
  // vertically centered inside the header band
  .headerAction {
    position: absolute;
    top: 0;
    right: 24px;
    height: 72px;
    display: flex;
    align-items: center;
    z-index: 30;
  }

  .stickerLayout {
    position: absolute;
    bottom: 8px;
    left: 8px;
  }


  .middleZone {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    width: 100%;
    container-type: size;
  }

  .chooseScreenLayout {
    margin: auto;
    position: relative;
    aspect-ratio: 16 / 9;
    // biggest 16/9 box fully visible in the middle zone, 16px of breathing room
    width: min(calc(100cqw - 32px), calc((100cqh - 32px) * 16 / 9));


    .timerBlock {
      width: 100px;
      border-radius: 8px;
      position: absolute;
      height: 20px;
      line-height: 20px;
      font-size: 12px;
      background: var(--black);
      // background: rgba(255, 255, 255, 0.8);
      color: var(--white);
      opacity: 0.8;
      bottom: 16px;
      left: calc(50% - 50px);
    }
  }


  // the timeline floats as a white card on the light background
  .timeLineView {
    position: relative;
    width: 100%;
    flex: none;
    padding: 0 16px 16px 16px;

    ::v-deep .timeLineBigContainer {
      background: var(--white);
      border-radius: 16px;
      box-shadow: 0 1px 4px rgba(10, 8, 47, 0.08);
    }
  }

  .countDownContainer {
    position: absolute;
    border-radius: 0;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    // over the header bar and its actions
    z-index: 35;
    background-color: rgba(27, 57, 84, 0.8);

    .counter {
      position: absolute;
      width: 100%;
      top: calc(50% - 150px);
      font-size: 190px;
      font-weight: 600;
      color: white;
      text-shadow: 2px 2px 10px #1B3954;
    }
  }

  .onBoardingRecord {
    position: absolute;
    left: 24px;
    bottom: 216px;
    z-index: 25;
    animation-duration: 0.1s;
  }

  .onBoardingSection {
    position: absolute;
    left: 24px;
    bottom: 216px;
    z-index: 25;
    animation-duration: 0.1s;
  }
}

</style>
